import { isMobileDevice } from "../compat";
import { GetOrCreateWebGPUDevice, IsCompatibilityMode } from "./wgpu/globals";

export const Kilobytes = (value) => value * 1024;
export const Megabytes = (value) => Kilobytes(value) * 1024;
export const Gigabytes = (value) => Megabytes(value) * 1024;

export let GPU_MEMORY_LIMIT = (isMobileDevice() ? 64 : 256) * 1024 * 1024;
export function setGPU_MEMORY_LIMIT(value) {
  GPU_MEMORY_LIMIT = value;
}

export let GPU_OBJECT_LIMIT = isMobileDevice() ? 2500 : 10000;
export let CONSOLIDATION_MEMORY_LIMIT = GPU_MEMORY_LIMIT / 2;

export function setCONSOLIDATION_MEMORY_LIMIT(value) {
  CONSOLIDATION_MEMORY_LIMIT = value;
}

// Default value for the amount of memory that we allow to consume before removing models from memory.
export let MODEL_MEMORY_LIMIT = 1000 * 1024 * 1024;

export const disableGpuObjectLimit = function () {GPU_OBJECT_LIMIT = 0xffffffff;};

//VAO objects do have quite a bit of memory overhead, so use of VAO can be optionally
//turned off
export let USE_VAO = !isMobileDevice();

// Overhead of a LeanBufferGeometry.
// Determined by: looking at shallow sizes in Chrome's Memory Profiler of all class instances divided by num instances
// LeanBufferGeometry: has 41 attributes with WebGPU (of those 3 JS internals), but actual reported size is 170B
// different TypedArrays (vb, ib, iblines): 60B each
// ArrayBuffer (backing buffer): 52B
// MemoryBlocks (_gpuIbBlock, _gpuVbBlock): 40B each
// {dispose} (_listeners): 28B
// {position,color,index} (attributes): doesn't seem to be deduplicated, 44B each (also includes {position, normal, color, index, indexlines})
// HeapNumber (importance): 12B
// Backing ArrayBuffer contains an OTG header. With a small set of attributes (position, color, index), that's 28B, usually there might be more
export const GEOMETRY_OVERHEAD = 594;

// This is the threshold of the projected screen pixel for culling.
export let PIXEL_CULLING_THRESHOLD = 1.5;

export function setPIXEL_CULLING_THRESHOLD(value) {
  PIXEL_CULLING_THRESHOLD = value;
}

// Maximum amount of time in ms we allow for consolidation per frame
export const PER_FRAME_CONSOLIDATION_TIME_BUDGET = 10;

export let USE_OUT_OF_CORE_TILE_MANAGER = false;

export let USE_GEOMETRY_OOC = false;

// Defines whether to use deferred consolidation by default
export let USE_DEFERRED_CONSOLIDATION = false;

// Defines whether to use the BufferManager
export let USE_BUFFER_MANAGER = false;

// DEPRECATED: Use HLOD based rendering and settings?
export let USE_HLOD = false;

// Cache BVH with fragment list and consolidation map in OPFS. Only works with out of core management. Set by enableOutOfCoreTileManager.
export let BVH_CACHING = false;

// Assume the websocket can deliver the bvh and fraglist chunk (the "f" message type)
export const USE_SERVER_SIDE_BVH = false;

// Force WebGPU and WebGPU Compatibility mode usage
export let USE_WEBGPU_COMPATIBILITY_MODE = false;

// Use WebGPU instead of WebGL
export let USE_WEBGPU = false;

// Use Weighted Blended Order-Independent Transparency
export let USE_WBOIT = false;

// Encode OTG vertices as UNORM16 (positions quantized, normals oct16-packed in W). Requires WebGPU, OTG only.
// Set by the Unorm16GeometryQuantization internal feature flag.
// When enabled, geometries are quantized by default. For fragments with large extents, the OtgLoader appends
// '-highp' to the hash and creates a new GeometryList entry. The OtgLoadWorker quantizes each geometry unless the hash contains '-highp'.
// The OPFS cache is unchanged and always stores the original gzipped geometries.
export let USE_UNORM16_GEOMETRY_QUANTIZATION = false;

// Max geometry bbox size (in meters) for UNORM16 quantization.
// Derived from max quantization error of 0.5mm: 0.0005m * 2 * 65535 = 65.535m
export const MAX_BBOX_SIZE_FOR_UNORM16 = 65.535;

// Maximum amount of time in ms we allow for tasks per frame
export const PER_FRAME_TASK_TIME_BUDGET_MAX = 15;

// The fraction of the time (between target and max frame time) that we allow for tasks
export const PER_FRAME_TASK_TIME_BUDGET_FACTOR = 0.25;

// Number of polygons in a mesh to allow consolidation for that mesh
export const DEFAULT_CONSOLIDATION_POLYCOUNT_LIMIT = 10000;

// While loading we limit the render budget to save CPU time
export const RENDER_BUDGET_SCALING_FACTOR_WHILE_LOADING = 0.125;

// Whether to copy the depth buffer to an overlay texture for use in the overlay pass.
export let USE_OVERLAY_DEPTH_COPY = false;

// Option to allow Tandem to avoid slow streaming draw - to fix sever performance issues for overlays.
//  - Must be set once before initializing the viewer
//  - Note that it bypasses GPU memory budget
export let DISABLE_STREAMING_DRAW = false;
export function disableStreamingDraw() {
  DISABLE_STREAMING_DRAW = true;
}

// Limits HW instancing to fragments in the same tile
/* DEPRECATED */export let PER_TILE_INSTANCING = false;

// Use Uniform Buffer Objects in WebGL to render fragments with compatible materials in the same draw call
// Only used with consolidated meshes
export let USE_MULTI_MATERIAL_RENDER_CALLS = false;

/* DEPRECATED */export let INCREMENTAL_CONSOLIDATION = false;

// Sizes of the regions that get allocated by the buffer manager
// 65536 is the maximum number of vertices that can be addressed with a 16 bit index buffer
// The additional factors account for used types and stored data, but are not necessarily optimal
export const VERTEX_BUFFER_REGION_SIZE = 65536 * 4 * 4;
export const INDEX_BUFFER_REGION_SIZE = 8 * 65536 * 2;

let USE_PROP_HASH_64 = false;
let USE_SKIP_SPARSE_ATTRIBUTE_DEFS = false;

/**
 * Feature Flag to enable/disable BufferManager usage
 * @param {Boolean} enable
 *
 * @experimental This function is experimental and may be changed or removed in future versions.
 */
export function useBufferManager(enable = true) {
  USE_BUFFER_MANAGER = enable;
  USE_VAO = !USE_BUFFER_MANAGER;
}

const DEFAULT_SETTINGS = {
  gpu_memory: (isMobileDevice() ? 256 : 2048) * 1024 * 1024,
  gpu_objects: 512_000,
  model_memory: 4 * 1024 * 1024 * 1024,
  consolidation_memory: 1024 * 1024 * 1024
};

export function useHLOD(enable = true, settings) {
  console.warn('useHLOD is deprecated. Please use enableOutOfCoreTileManager instead.');
  if (enable) {
    enableOutOfCoreTileManager(settings);
  }
}

/**
 * Feature Flag to enable/disable out of core management
 * @param {Object} settings
 * @param {Number} settings.gpu_memory - The amount of GPU memory to use
 * @param {Number} settings.gpu_objects - The amount of GPU objects to use
 * @param {Number} settings.model_memory - The amount of model memory to use
 * @param {Number} settings.consolidation_memory - The amount of consolidation memory to use
 */
export function enableOutOfCoreTileManager(settings = DEFAULT_SETTINGS) {
  USE_HLOD = true;
  useBufferManager(true);
  USE_DEFERRED_CONSOLIDATION = true;

  GPU_MEMORY_LIMIT = settings.gpu_memory;
  GPU_OBJECT_LIMIT = settings.gpu_objects;
  MODEL_MEMORY_LIMIT = settings.model_memory;
  CONSOLIDATION_MEMORY_LIMIT = settings.consolidation_memory;

  console.log(`Using HLOD settings. Memory: ${GPU_MEMORY_LIMIT}, Object Limit: ${GPU_OBJECT_LIMIT}, Consolidation Memory: ${CONSOLIDATION_MEMORY_LIMIT}`);

  USE_OUT_OF_CORE_TILE_MANAGER = true;
  BVH_CACHING = true;
}

let webGPUDeviceRequestRunning = false;
export function enableUBOs() {
  // UBOs are a WebGL optimization, skip if WebGPU is active or being initialized.
  // If WebGPU fails, USE_WEBGPU=false and UBOs are correctly enabled for WebGL fallback.
  const isWebGLWithOutOfCore = USE_OUT_OF_CORE_TILE_MANAGER && !(USE_WEBGPU || webGPUDeviceRequestRunning);
  if (isWebGLWithOutOfCore) {
    USE_MULTI_MATERIAL_RENDER_CALLS = true;
    console.log(`Using UBOs for multi-material rendering`);
  }
}

/**
 * Attempts to initialize WebGPU. If successful, Viewer instances will use WebGPU instead of WebGL.
 * On failure, falls back to WebGL and logs a warning.
 *
 * @param {GPURequestAdapterOptions} [adapterOptions] - Passed to navigator.gpu.requestAdapter
 * @param {GPUSupportedFeatures} [deviceFeatures] - Passed to adapter.requestDevice
 * @param {boolean} [forceCompatibility=false] - Force WebGPU compatibility mode
 * @returns {Promise<boolean>} True if WebGPU was successfully initialized, false otherwise
 *
 * @experimental This function is experimental and may be changed or removed in future versions.
 */
export async function useWebGPU(adapterOptions, deviceFeatures, forceCompatibility = false) {

  webGPUDeviceRequestRunning = true; // to sync with enableUBOs for WebGL
  const device = await GetOrCreateWebGPUDevice(adapterOptions, deviceFeatures, forceCompatibility);
  webGPUDeviceRequestRunning = false;

  // Reflect WebGPU status and capabilities back into the respective global flags
  USE_WEBGPU = Boolean(device);
  USE_WEBGPU_COMPATIBILITY_MODE = IsCompatibilityMode();

  if (USE_WEBGPU === false) {
    console.warn('WebGPU initialization failed, falling back to WebGL.');
    return false;
  }
  // OutOfCoreTileManager should always be enabled for WebGPU.
  if (!USE_OUT_OF_CORE_TILE_MANAGER) {
    enableOutOfCoreTileManager();
  }
  return true;
}

export function propHash64Enabled(use) {
  if (typeof use !== "boolean") return USE_PROP_HASH_64;
  USE_PROP_HASH_64 = use;
  return USE_PROP_HASH_64;
}

export function skipSparseAttributeDefinitions(use) {
  if (typeof use !== "boolean") return USE_SKIP_SPARSE_ATTRIBUTE_DEFS;
  USE_SKIP_SPARSE_ATTRIBUTE_DEFS = use;
  return USE_SKIP_SPARSE_ATTRIBUTE_DEFS;
}

// Minimum time in milliseconds attributed to rendering a single batch.
// Measured batch durations are clamped to this value from below, preventing the frame-time
// estimator from receiving zero deltas that would produce bogus results.
// Because the frame has a fixed time budget (MAX_FRAME_BUDGET) and each batch is charged at
// least MIN_BATCH_TIME_MS, this constant also acts as an implicit upper bound on the number
// of batches that can be rendered in one frame: at most MAX_FRAME_BUDGET / MIN_BATCH_TIME_MS.
// Note: performance.now() has an effective granularity of ~0.1ms, so measured deltas can be 0.
// We intentionally choose a smaller value (0.01ms) to allow more batches per frame while still
// avoiding the degenerate case of a zero delta.
export const MIN_BATCH_TIME_MS = 0.01;

// Assuming 24 bits depth resolution this should be a working epsilon to distinguish between individual depth values
export const DEPTH_EPSILON = 2 * Math.pow(2, -24);

// The geometry ID 0 means that the fragment does not have a geometry.
// It might still have a material, but it will never be rendered
// E.g. the OTG translation assigns this when the geometry was degenerated.
export const INVALID_GEOM_ID = 0;

// The fragment does not have a material. It's unclear when this happens.
export const INVALID_MAT_ID = 0;

// Material ID for the single RCS point cloud material. RCS models have exactly
// one material (num_materials: 1 in RcsPackage), so this is the first and only
// valid material ID (1-indexed, matching the SVF/OTG convention where 0 is INVALID_MAT_ID).
export const RCS_POINTCLOUD_MATERIAL_ID = 1;


// These values were determined experimentally. After trying different values
// these seemed to provide the best render completion times when waiting for
// streaming draws to finish.
// 2mb staging buffers is based on using 10 staging buffers at 30fps, which is
// the amount of queue submits we aim for at 30fps. It also seemed to work best
// while experimenting.
export let MAX_BYTES_PER_SECOND = Megabytes(600);
export function setMAX_BYTES_PER_SECOND(value) {
  MAX_BYTES_PER_SECOND = value;
}

export const MAPPED_BUF_SIZE = Megabytes(2);

export function enableOverlayDepthCopy() {
  USE_OVERLAY_DEPTH_COPY = true;
}

export function enableWboit() {
  if (USE_WEBGPU) {
    USE_WBOIT = true;
  }
}

export function enableUnorm16GeometryQuantization() {
  if (USE_WEBGPU) {
    USE_UNORM16_GEOMETRY_QUANTIZATION = true;
  }
}

// Used by Viewer3DImpl.rayIntersect as a screen space pixel tolerance for line and point hit tests.
export const RAY_INTERSECT_HIT_MARGIN_PIXELS = 2;